import { useState, useEffect, useCallback, useRef } from "react";
import { api } from "../App";
import { getApiPrefix } from "../utils/apiHelper";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import DataTable from "../components/ui/data-table";
import { handleApiError, showSuccess } from "../utils/errorHandler";
import { PageLoader } from "../components/ui/page-loader";
import { Plus, Trash2, Loader2, Calendar, CheckCircle, Pencil, Banknote, Building, Car, ListPlus, Upload, FileSpreadsheet } from "lucide-react";
import { EXCEL_COLUMNS } from "../components/ui/excel-upload-helper";
import { useTranslation } from "../contexts/I18nContext";

const TIPI_SCADENZA = [
  { value: "prestito", label: "Prestito", icon: Banknote },
  { value: "finanziamento", label: "Finanziamento", icon: Building },
  { value: "leasing", label: "Leasing", icon: Car },
  { value: "altro", label: "Altro", icon: Calendar },
];

const PERIODICITA = [
  { value: "mensile", label: "Mensile" },
  { value: "bimestrale", label: "Bimestrale" },
  { value: "trimestrale", label: "Trimestrale" },
  { value: "semestrale", label: "Semestrale" },
  { value: "annuale", label: "Annuale" },
];

export default function ScadenzeRicorrentiPage() {
  const { t } = useTranslation();
  const [scadenze, setScadenze] = useState([]);
  const [associazioniByPaymentId, setAssociazioniByPaymentId] = useState({});
  const [loading, setLoading] = useState(true);
  const [filterTipo, setFilterTipo] = useState("");
  const [filterStato, setFilterStato] = useState("");
  
  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogTab, setDialogTab] = useState("singola");
  const [editingId, setEditingId] = useState(null);
  
  // Single payment form
  const [form, setForm] = useState({
    descrizione: "",
    importo: "",
    data_scadenza: "",
    tipo: "prestito",
    beneficiario: "",
    numero_rata: "",
    totale_rate: "",
    note: ""
  });
  
  // Piano ammortamento form
  const [pianoForm, setPianoForm] = useState({
    descrizione: "",
    importo_rata: "",
    data_prima_rata: "",
    numero_rate: "",
    periodicita: "mensile",
    tipo: "prestito",
    beneficiario: "",
    note: ""
  });
  
  // Upload Excel form
  const [uploadForm, setUploadForm] = useState({
    descrizione: "",
    tipo: "prestito",
    beneficiario: ""
  });
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null, descrizione: "" });

  const fetchScadenze = useCallback(async () => {
    try {
      const prefix = getApiPrefix();
      const params = new URLSearchParams();
      if (filterTipo && filterTipo !== "all") params.append("tipo", filterTipo);
      if (filterStato && filterStato !== "all") params.append("stato", filterStato);
      const [res, allocRes] = await Promise.all([
        api.get(`${prefix}/scheduled-payments?${params.toString()}`),
        api.get(`${prefix}/scheduled-payments/rate-allocabili`).catch(() => ({ data: [] })),
      ]);
      // Filter out affitto type - those belong to Appartamenti
      setScadenze(res.data.filter(s => s.tipo !== 'affitto'));
      // Costruisci mappa scheduled_payment_id -> info movimento collegato
      const assocMap = {};
      for (const piano of (allocRes.data || [])) {
        for (const r of (piano.rate || [])) {
          if (r.is_associata) {
            assocMap[r.id] = {
              movement_id: r.linked_movement_id,
              movement_data: r.movement_data,
              movement_descrizione: r.movement_descrizione,
              movement_importo: r.movement_importo,
            };
          }
        }
      }
      setAssociazioniByPaymentId(assocMap);
    } catch (err) {
      handleApiError(err, t("errors.loadDeadlines"));
    } finally {
      setLoading(false);
    }
  }, [filterTipo, filterStato, t]);

  useEffect(() => {
    fetchScadenze();
  }, [fetchScadenze]);

  const formatCurrency = (val) =>
    new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(val || 0);

  const openAddDialog = () => {
    setEditingId(null);
    setDialogTab("singola");
    setForm({
      descrizione: "",
      importo: "",
      data_scadenza: "",
      tipo: "prestito",
      beneficiario: "",
      numero_rata: "",
      totale_rate: "",
      note: ""
    });
    setPianoForm({
      descrizione: "",
      importo_rata: "",
      data_prima_rata: "",
      numero_rate: "",
      periodicita: "mensile",
      tipo: "prestito",
      beneficiario: "",
      note: ""
    });
    setDialogOpen(true);
  };

  const openEditDialog = (item) => {
    setEditingId(item.id);
    setDialogTab("singola");
    setForm({
      descrizione: item.descrizione || "",
      importo: String(item.importo || ""),
      data_scadenza: item.data_scadenza || "",
      tipo: item.tipo || "altro",
      beneficiario: item.beneficiario || "",
      numero_rata: item.numero_rata ? String(item.numero_rata) : "",
      totale_rate: item.totale_rate ? String(item.totale_rate) : "",
      note: item.note || ""
    });
    setDialogOpen(true);
  };

  const handleSubmitSingle = async () => {
    if (!form.descrizione || !form.importo || !form.data_scadenza) {
      handleApiError({ message: t("errors.requiredDescriptionDeadline") });
      return;
    }
    try {
      const prefix = getApiPrefix();
      const payload = {
        descrizione: form.descrizione,
        importo: parseFloat(form.importo),
        data_scadenza: form.data_scadenza,
        tipo: form.tipo,
        beneficiario: form.beneficiario || null,
        numero_rata: form.numero_rata ? parseInt(form.numero_rata) : null,
        totale_rate: form.totale_rate ? parseInt(form.totale_rate) : null,
        note: form.note || null
      };
      
      if (editingId) {
        await api.put(`${prefix}/scheduled-payments/${editingId}`, payload);
        showSuccess(t("success.deadlineUpdated"));
      } else {
        await api.post(`${prefix}/scheduled-payments`, payload);
        showSuccess(t("success.deadlineCreated"));
      }
      setDialogOpen(false);
      fetchScadenze();
    } catch (err) {
      handleApiError(err, t("errors.saveFailed"));
    }
  };

  const [creatingPiano, setCreatingPiano] = useState(false);

  const handleSubmitPiano = async () => {
    if (!pianoForm.descrizione || !pianoForm.importo_rata || !pianoForm.data_prima_rata || !pianoForm.numero_rate) {
      handleApiError({ message: t("errors.requiredFields") });
      return;
    }
    setCreatingPiano(true);
    try {
      const prefix = getApiPrefix();
      const payload = {
        descrizione: pianoForm.descrizione,
        importo_rata: parseFloat(pianoForm.importo_rata),
        data_prima_rata: pianoForm.data_prima_rata,
        numero_rate: parseInt(pianoForm.numero_rate),
        periodicita: pianoForm.periodicita,
        tipo: pianoForm.tipo,
        beneficiario: pianoForm.beneficiario || null,
        note: pianoForm.note || null
      };
      
      const res = await api.post(`${prefix}/scheduled-payments/piano-ammortamento`, payload);
      showSuccess(res.data.message);
      setDialogOpen(false);
      fetchScadenze();
    } catch (err) {
      handleApiError(err, t("errors.errorCreatePlan"));
    } finally {
      setCreatingPiano(false);
    }
  };

  const handleUploadExcel = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!uploadForm.descrizione) {
      handleApiError(new Error(t("errors.requiredPlanDescription")), t("errors.requiredPlanDescription"));
      return;
    }
    
    setUploading(true);
    try {
      const prefix = getApiPrefix();
      const formData = new FormData();
      formData.append("file", file);
      formData.append("descrizione", uploadForm.descrizione);
      formData.append("tipo", uploadForm.tipo);
      if (uploadForm.beneficiario) {
        formData.append("beneficiario", uploadForm.beneficiario);
      }
      
      const res = await api.post(`${prefix}/scheduled-payments/upload-piano`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      showSuccess(res.data.message);
      setDialogOpen(false);
      setUploadForm({ descrizione: "", tipo: "prestito", beneficiario: "" });
      fetchScadenze();
    } catch (err) {
      handleApiError(err, t("errors.errorUploadPlan"));
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleDelete = async () => {
    try {
      const prefix = getApiPrefix();
      await api.delete(`${prefix}/scheduled-payments/${deleteDialog.id}`);
      showSuccess(t("success.deadlineDeleted"));
      setDeleteDialog({ open: false, id: null, descrizione: "" });
      fetchScadenze();
    } catch (err) {
      handleApiError(err, t("errors.deleteFailed"));
    }
  };

  const handleBulkDelete = async (ids) => {
    let deleted = 0;
    let errors = 0;
    const prefix = getApiPrefix();
    
    for (const id of ids) {
      try {
        await api.delete(`${prefix}/scheduled-payments/${id}`);
        deleted++;
      } catch {
        errors++;
      }
    }
    
    if (deleted > 0) {
      showSuccess(`${deleted} scadenze eliminate`);
    }
    if (errors > 0) {
      handleApiError({ message: `${errors} errori durante l'eliminazione` });
    }
    
    fetchScadenze();
  };

  const handleMarkPaid = async (id) => {
    try {
      const prefix = getApiPrefix();
      await api.patch(`${prefix}/scheduled-payments/${id}/pay`);
      showSuccess(t("success.deadlineMarkedPaid"));
      fetchScadenze();
    } catch (err) {
      handleApiError(err, t("errors.updateFailed"));
    }
  };

  const getTipoBadge = (tipo) => {
    const config = {
      prestito: { className: "bg-blue-100 text-blue-700", label: "Prestito" },
      finanziamento: { className: "bg-purple-100 text-purple-700", label: "Finanziamento" },
      leasing: { className: "bg-orange-100 text-orange-700", label: "Leasing" },
      altro: { className: "bg-gray-100 text-gray-700", label: "Altro" },
    };
    const c = config[tipo] || config.altro;
    return <Badge variant="outline" className={c.className}>{c.label}</Badge>;
  };

  const getStatoBadge = (stato) => {
    if (stato === "pagata") {
      return <Badge className="bg-green-100 text-green-700">{t('invoices.paid')}</Badge>;
    }
    return <Badge variant="destructive">{t('common.toPay')}</Badge>;
  };

  // Stats
  const totaleDaPagare = scadenze.filter(s => s.stato === "da_pagare").reduce((sum, s) => sum + s.importo, 0);
  const countDaPagare = scadenze.filter(s => s.stato === "da_pagare").length;
  const countPagate = scadenze.filter(s => s.stato === "pagata").length;

  // Definizione colonne per DataTable
  const columns = [
    {
      key: 'tipo',
      label: t('common.type'),
      render: (value) => getTipoBadge(value)
    },
    {
      key: 'data_scadenza',
      label: 'Data Scadenza',
      render: (value) => <span className="font-mono">{value}</span>
    },
    {
      key: 'descrizione',
      label: t('common.description'),
      render: (value) => <span className="max-w-[200px] truncate font-medium block">{value}</span>
    },
    {
      key: 'beneficiario',
      label: 'Beneficiario',
      render: (value) => <span className="text-sm">{value || "-"}</span>
    },
    {
      key: 'rata',
      label: 'Rata',
      getValue: (row) => row.numero_rata && row.totale_rate ? `${row.numero_rata}/${row.totale_rate}` : '-',
      render: (value) => <span className="text-sm">{value}</span>
    },
    {
      key: 'importo',
      label: t('common.amount'),
      align: 'right',
      format: 'currency'
    },
    {
      key: 'stato',
      label: t('common.status'),
      render: (value, row) => {
        const assoc = associazioniByPaymentId[row.id];
        return (
          <div className="flex flex-col gap-1">
            {getStatoBadge(value)}
            {assoc && (
              <Badge
                variant="outline"
                className="text-[10px] text-emerald-700 border-emerald-300 bg-emerald-50 cursor-help"
                title={`${assoc.movement_data || ''} · ${assoc.movement_descrizione || ''} · ${assoc.movement_importo != null ? '€ ' + Math.abs(assoc.movement_importo).toFixed(2) : ''}`}
                data-testid={`alloc-badge-${row.id}`}
              >
                <Banknote className="h-3 w-3 mr-1" />{t("common.linkedMovement")}
              </Badge>
            )}
          </div>
        );
      }
    },
    {
      key: 'actions',
      label: t('common.actions'),
      align: 'right',
      sortable: false,
      filterable: false,
      render: (_, row) => (
        <div className="flex justify-end gap-1">
          {row.stato === "da_pagare" && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleMarkPaid(row.id)}
              title="Segna come pagata"
              className="text-green-600"
            >
              <CheckCircle className="h-4 w-4" />
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={() => openEditDialog(row)}>
            <Pencil className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setDeleteDialog({ open: true, id: row.id, descrizione: row.descrizione })}
            className="text-destructive"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      )
    }
  ];

  if (loading) {
    return <PageLoader variant="section" text={t("common.loading")} />;
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="scadenze-ricorrenti-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">{t("nav.recurringDeadlines")}</h1>
          <p className="text-muted-foreground">Gestione piani di ammortamento, prestiti e finanziamenti</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Calendar className="h-8 w-8 text-red-500" />
              <div>
                <p className="text-xs text-muted-foreground uppercase">{t('common.toPay')}</p>
                <p className="text-2xl font-bold">{countDaPagare}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div>
              <p className="text-xs text-muted-foreground uppercase">Totale Da Pagare</p>
              <p className="text-2xl font-bold font-mono text-red-600">{formatCurrency(totaleDaPagare)}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <CheckCircle className="h-8 w-8 text-green-500" />
              <div>
                <p className="text-xs text-muted-foreground uppercase">{t('invoices.paidPlural')}</p>
                <p className="text-2xl font-bold text-green-600">{countPagate}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex gap-4 flex-wrap">
            <Select value={filterTipo} onValueChange={setFilterTipo}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder={t("common.allTypes")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('common.allTypes')}</SelectItem>
                {TIPI_SCADENZA.map(t => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterStato} onValueChange={setFilterStato}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder={t("common.allStatuses")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('common.allStatusesText')}</SelectItem>
                <SelectItem value="da_pagare">{t('common.toPay')}</SelectItem>
                <SelectItem value="pagata">{t('invoices.paid')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table with DataTable component */}
      <Card>
        <CardHeader>
          <CardTitle>Elenco Scadenze ({scadenze.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <DataTable
            columns={columns}
            data={scadenze}
            onDelete={handleBulkDelete}
            idField="id"
            emptyMessage="Nessuna scadenza trovata"
            toolbar={
              <Button onClick={openAddDialog} data-testid="add-scadenza-btn">
                <Plus className="h-4 w-4 mr-2" />{t('invoices.newDueDate')}</Button>
            }
          />
        </CardContent>
      </Card>

      {/* Add/Edit Dialog with Tabs */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingId ? t("scheduler.editDeadline") : t("scheduler.newDeadline")}</DialogTitle>
          </DialogHeader>
          
          {!editingId ? (
            <Tabs value={dialogTab} onValueChange={setDialogTab}>
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="singola">Scadenza Singola</TabsTrigger>
                <TabsTrigger value="piano">Piano Ammortamento</TabsTrigger>
                <TabsTrigger value="upload">Upload Excel</TabsTrigger>
              </TabsList>
              
              <TabsContent value="singola" className="space-y-4 mt-4">
                {/* Single payment form */}
                <div className="space-y-2">
                  <Label>{t('common.type')} *</Label>
                  <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {TIPI_SCADENZA.map(t => (
                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Descrizione *</Label>
                  <Input
                    value={form.descrizione}
                    onChange={(e) => setForm({ ...form, descrizione: e.target.value })}
                    placeholder="Es: Mutuo casa"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t('common.amount')} *</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={form.importo}
                      onChange={(e) => setForm({ ...form, importo: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Data Scadenza *</Label>
                    <Input
                      type="date"
                      value={form.data_scadenza}
                      onChange={(e) => setForm({ ...form, data_scadenza: e.target.value })}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>{t('common.beneficiary')}</Label>
                  <Input
                    value={form.beneficiario}
                    onChange={(e) => setForm({ ...form, beneficiario: e.target.value })}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>N. Rata</Label>
                    <Input
                      type="number"
                      value={form.numero_rata}
                      onChange={(e) => setForm({ ...form, numero_rata: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Totale Rate</Label>
                    <Input
                      type="number"
                      value={form.totale_rate}
                      onChange={(e) => setForm({ ...form, totale_rate: e.target.value })}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>{t("common.notes")}</Label>
                  <Textarea
                    value={form.note}
                    onChange={(e) => setForm({ ...form, note: e.target.value })}
                  />
                </div>
                <Button onClick={handleSubmitSingle} className="w-full">Crea Scadenza</Button>
              </TabsContent>
              
              <TabsContent value="piano" className="space-y-4 mt-4">
                {/* Piano ammortamento form */}
                <Card className="bg-blue-50 border-blue-200">
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-2 text-blue-700">
                      <ListPlus className="h-5 w-5" />
                      <span className="text-sm font-medium">Genera automaticamente tutte le rate del piano</span>
                    </div>
                  </CardContent>
                </Card>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t('common.type')} *</Label>
                    <Select value={pianoForm.tipo} onValueChange={(v) => setPianoForm({ ...pianoForm, tipo: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {TIPI_SCADENZA.map(t => (
                          <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Periodicità *</Label>
                    <Select value={pianoForm.periodicita} onValueChange={(v) => setPianoForm({ ...pianoForm, periodicita: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {PERIODICITA.map(p => (
                          <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Descrizione *</Label>
                  <Input
                    value={pianoForm.descrizione}
                    onChange={(e) => setPianoForm({ ...pianoForm, descrizione: e.target.value })}
                    placeholder="Es: Mutuo casa via Roma"
                    data-testid="piano-descrizione"
                  />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Importo Rata *</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={pianoForm.importo_rata}
                      onChange={(e) => setPianoForm({ ...pianoForm, importo_rata: e.target.value })}
                      data-testid="piano-importo-rata"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>N. Rate *</Label>
                    <Input
                      type="number"
                      min="1"
                      max="360"
                      value={pianoForm.numero_rate}
                      onChange={(e) => setPianoForm({ ...pianoForm, numero_rate: e.target.value })}
                      placeholder="Es: 48"
                      data-testid="piano-numero-rate"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Data Prima Rata *</Label>
                    <Input
                      type="date"
                      value={pianoForm.data_prima_rata}
                      onChange={(e) => setPianoForm({ ...pianoForm, data_prima_rata: e.target.value })}
                      data-testid="piano-data-prima-rata"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>{t('common.beneficiary')}</Label>
                  <Input
                    value={pianoForm.beneficiario}
                    onChange={(e) => setPianoForm({ ...pianoForm, beneficiario: e.target.value })}
                    placeholder="Es: Banca XYZ"
                    data-testid="piano-beneficiario"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t("common.notes")}</Label>
                  <Textarea
                    value={pianoForm.note}
                    onChange={(e) => setPianoForm({ ...pianoForm, note: e.target.value })}
                  />
                </div>

                {/* Anteprima Riepilogo Piano */}
                {pianoForm.importo_rata && pianoForm.numero_rate && pianoForm.data_prima_rata && (
                  <Card className="bg-slate-50 border-slate-200">
                    <CardContent className="pt-4">
                      <p className="text-xs text-muted-foreground uppercase mb-2">Riepilogo Piano</p>
                      <div className="grid grid-cols-3 gap-2 text-sm">
                        <div>
                          <span className="text-muted-foreground">Rate:</span>{" "}
                          <span className="font-semibold">{pianoForm.numero_rate}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Rata:</span>{" "}
                          <span className="font-semibold">
                            {new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(parseFloat(pianoForm.importo_rata) || 0)}
                          </span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">{t("common.total")}:</span>{" "}
                          <span className="font-bold text-blue-700">
                            {new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(
                              (parseFloat(pianoForm.importo_rata) || 0) * (parseInt(pianoForm.numero_rate) || 0)
                            )}
                          </span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                <Button
                  onClick={handleSubmitPiano}
                  className="w-full"
                  disabled={creatingPiano || !pianoForm.descrizione || !pianoForm.importo_rata || !pianoForm.data_prima_rata || !pianoForm.numero_rate}
                  data-testid="piano-submit-btn"
                >
                  {creatingPiano ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Creazione in corso...</>
                  ) : (
                    <><ListPlus className="h-4 w-4 mr-2" /> Genera Piano Ammortamento</>
                  )}
                </Button>
              </TabsContent>
              
              <TabsContent value="upload" className="space-y-4 mt-4">
                {/* Upload Excel form */}
                <Card className="bg-amber-50 border-amber-200">
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-2 text-amber-700">
                      <FileSpreadsheet className="h-5 w-5" />
                      <span className="text-sm font-medium">Carica piano con rate variabili da Excel</span>
                    </div>
                    <p className="text-xs text-amber-600 mt-1">
                      Il file deve contenere le colonne: data_scadenza, importo
                    </p>
                  </CardContent>
                </Card>
                
                <div className="space-y-2">
                  <Label>{t('common.type')} *</Label>
                  <Select value={uploadForm.tipo} onValueChange={(v) => setUploadForm({ ...uploadForm, tipo: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {TIPI_SCADENZA.map(t => (
                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Descrizione Piano *</Label>
                  <Input
                    value={uploadForm.descrizione}
                    onChange={(e) => setUploadForm({ ...uploadForm, descrizione: e.target.value })}
                    placeholder="Es: Leasing Auto Aziendale"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('common.beneficiary')}</Label>
                  <Input
                    value={uploadForm.beneficiario}
                    onChange={(e) => setUploadForm({ ...uploadForm, beneficiario: e.target.value })}
                    placeholder="Es: Banca XYZ"
                  />
                </div>
                <div className="space-y-2">
                  <Label>File Excel (.xlsx, .xls)</Label>
                  <Input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleUploadExcel}
                    disabled={uploading || !uploadForm.descrizione}
                    className="cursor-pointer"
                  />
                </div>
                {uploading && (
                  <div className="flex items-center justify-center gap-2 text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>Caricamento in corso...</span>
                  </div>
                )}
                <div className="p-3 bg-muted rounded-md text-xs space-y-2">
                  <p className="font-medium">Formato file atteso:</p>
                  <table className="w-full text-left">
                    <thead>
                      <tr className="border-b">
                        {EXCEL_COLUMNS.scadenze_ricorrenti.filter(c => ["data_scadenza", "importo", "note"].includes(c.name) || c.name === "descrizione").slice(0, 3).map(col => (
                          <th key={col.name} className="pb-1">{col.name}{!col.required && " (opz.)"}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="pt-1">01/04/2026</td>
                        <td className="pt-1">1500.00</td>
                        <td className="pt-1">Rata 1</td>
                      </tr>
                      <tr>
                        <td>01/05/2026</td>
                        <td>1450.50</td>
                        <td>Rata 2</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </TabsContent>
            </Tabs>
          ) : (
            /* Edit mode - single form only */
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>{t('common.type')} *</Label>
                <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TIPI_SCADENZA.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Descrizione *</Label>
                <Input
                  value={form.descrizione}
                  onChange={(e) => setForm({ ...form, descrizione: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t('common.amount')} *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={form.importo}
                    onChange={(e) => setForm({ ...form, importo: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Data Scadenza *</Label>
                  <Input
                    type="text"
                    value={form.data_scadenza}
                    onChange={(e) => setForm({ ...form, data_scadenza: e.target.value })}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>{t('common.beneficiary')}</Label>
                <Input
                  value={form.beneficiario}
                  onChange={(e) => setForm({ ...form, beneficiario: e.target.value })}
                />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setDialogOpen(false)}>{t("common.cancel")}</Button>
                <Button onClick={handleSubmitSingle}>{t("common.update")}</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, id: null, descrizione: "" })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("confirm.confirmDeletion")}</DialogTitle>
            <DialogDescription>
              Eliminare la scadenza &quot;{deleteDialog.descrizione}&quot;?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog({ open: false, id: null, descrizione: "" })}>{t('common.cancel')}</Button>
            <Button variant="destructive" onClick={handleDelete}>{t("common.delete")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
